/**
 * Automatic update at a safe moment, and recovery from missing chunks.
 *
 * Every deploy deletes the previous build's files on the server. An open page
 * keeps working only because the OLD service worker still serves the old
 * files from its precache, and activating the new worker removes them. So the
 * new worker is activated only at a safe moment, and the page reloads as soon
 * as it has taken control (controllerchange, src/registerServiceWorker.js).
 *
 * Safe moments, only while an update is waiting (or another tab activated it):
 *  - a route change to another page, after the page being left is gone;
 *  - coming back to the foreground after at least 30 s away, unless the user
 *    typed something on this page since the last route change.
 * Not safe: offline, unsaved form input (store unsavedChanges), queued Weld
 * Mapping registrations. Automatic reloads are at least 10 s apart.
 *
 * The store is passed in by installAppUpdate() instead of imported, because
 * store/common.js imports this file.
 */
import Vue from 'vue'
import * as Sentry from '@sentry/vue'

const RELOAD_AT_KEY = 'weldit_sw_reload_at'
const INITIATED_KEY = 'weldit_update_initiated'
// Owned by WeldMappingDrawing.vue; read only here.
const OUTBOX_KEY = 'weldit-field-weld-outbox'
const RELOAD_GUARD_MS = 10 * 1000
const ACTIVATION_TIMEOUT_MS = 10 * 1000
const MIN_AWAY_MS = 30 * 1000
const NOTICE_INTERVAL_MS = 5 * 1000
const CHUNK_ERROR_MESSAGE = /Loading (CSS )?chunk .* failed/i

let store = null
let router = null
let initiatedTarget = null
let activationTimer = null
let typedSinceRouteChange = false
let hiddenAt = null
let lastNoticeAt = 0
let chunkErrorHandledAt = 0
// The user confirmed discarding unsaved input (header refresh button).
let discardConfirmed = false

export function currentUrl () {
  return window.location.pathname + window.location.search + window.location.hash
}

export function isChunkLoadError (err) {
  return !!err && (err.name === 'ChunkLoadError' || CHUNK_ERROR_MESSAGE.test(String(err.message || '')))
}

function hasUnsavedChanges () {
  return !!store && store.getters['unsavedChanges/hasUnsavedChanges']
}

function isOffline () {
  return navigator.onLine === false
}

function outboxPending () {
  try {
    const list = JSON.parse(window.localStorage.getItem(OUTBOX_KEY) || '[]')
    return Array.isArray(list) && list.length > 0
  } catch (e) {
    return false
  }
}

function waitingWorker () {
  const registration = store && store.state.common.activeSW
  return (registration && registration.waiting) || null
}

function updatePending () {
  return !!store && (store.state.common.updateActivatedElsewhere || !!waitingWorker())
}

// Claims the next automatic reload. False within 10 s of the last one (loop
// guard), and when sessionStorage is unavailable (then no automatic reload).
function takeReloadSlot () {
  try {
    const last = Number(window.sessionStorage.getItem(RELOAD_AT_KEY)) || 0
    if (Date.now() - last < RELOAD_GUARD_MS) return false
    window.sessionStorage.setItem(RELOAD_AT_KEY, String(Date.now()))
    return true
  } catch (e) {
    return false
  }
}

function recordReload () {
  try { window.sessionStorage.setItem(RELOAD_AT_KEY, String(Date.now())) } catch (e) { /* storage unavailable */ }
}

/**
 * Full page load of url. location.assign() alone only scrolls when url
 * differs from the current address by its hash.
 */
export function loadPage (url) {
  const next = new URL(url, window.location.href)
  if (next.pathname + next.search === window.location.pathname + window.location.search) {
    if (next.hash !== window.location.hash) window.history.replaceState(window.history.state, '', next.href)
    window.location.reload()
  } else {
    window.location.assign(next.href)
  }
}

function clearInitiated () {
  initiatedTarget = null
  clearTimeout(activationTimer)
  try { window.sessionStorage.removeItem(INITIATED_KEY) } catch (e) { /* storage unavailable */ }
}

/**
 * Called on controllerchange. Returns the URL to load when this tab started
 * the activation (applyUpdate), else null: another tab activated the worker.
 */
export function consumeInitiatedUpdate () {
  let target = initiatedTarget
  try { target = window.sessionStorage.getItem(INITIATED_KEY) || target } catch (e) { /* storage unavailable */ }
  clearInitiated()
  return target
}

/**
 * Switches to the new build, ending on targetUrl.
 *  - another tab already activated the new worker: load targetUrl;
 *  - a worker is waiting: activate it; controllerchange then loads targetUrl.
 *    No controllerchange within 10 s: give up until the next safe moment.
 * Automatic calls go through the 10 s reload guard. manual (header refresh
 * button) skips the guard and reloads even when no update is pending.
 * Returns true when a reload is on its way.
 */
export function applyUpdate (targetUrl, { manual = false } = {}) {
  if (!store) return false
  const elsewhere = store.state.common.updateActivatedElsewhere
  const waiting = waitingWorker()
  if (!manual && !elsewhere && !waiting) return false
  if (manual) {
    discardConfirmed = true
    recordReload()
  } else if (!takeReloadSlot()) {
    return false
  }
  if (elsewhere || !waiting) {
    loadPage(targetUrl)
    return true
  }
  initiatedTarget = targetUrl
  try { window.sessionStorage.setItem(INITIATED_KEY, targetUrl) } catch (e) { /* the in-memory copy is enough */ }
  clearTimeout(activationTimer)
  activationTimer = setTimeout(() => {
    clearInitiated()
    discardConfirmed = false
  }, ACTIVATION_TIMEOUT_MS)
  waiting.postMessage({ type: 'SKIP_WAITING' })
  return true
}

function tryAutoUpdate (targetUrl) {
  if (!updatePending() || isOffline() || hasUnsavedChanges() || outboxPending()) return false
  return applyUpdate(targetUrl)
}

function showSaveFirstNotice () {
  const now = Date.now()
  if (now - lastNoticeAt < NOTICE_INTERVAL_MS) return
  lastNoticeAt = now
  const root = router && router.app && router.app.$root
  if (root) root.$emit('snackbar', { snackbar: true, color: 'info', text: 'message.common.updateAfterSave' })
}

/**
 * A file of the running build is gone (deploy). Loads url in full, which
 * loads the new build, through the shared reload guard. Not while there is
 * unsaved input or the device is offline: then a snackbar asks the user to
 * save. Returns true when a reload is on its way; otherwise the caller lets
 * the error through (Sentry).
 */
export function recoverFromChunkError (url) {
  chunkErrorHandledAt = Date.now()
  if (isOffline() || hasUnsavedChanges()) {
    showSaveFirstNotice()
    return false
  }
  if (!takeReloadSlot()) return false
  loadPage(url)
  return true
}

function isBuildFile (url) {
  try {
    const parsed = new URL(url, window.location.href)
    return parsed.origin === window.location.origin && /^\/(js|css)\//.test(parsed.pathname)
  } catch (e) {
    return false
  }
}

function warnBeforeUnload (event) {
  if (discardConfirmed) return
  event.preventDefault()
  event.returnValue = ''
}

function installChunkErrorHandlers () {
  // Runs before Sentry.init (main.js imports the router first), so Sentry's
  // Vue integration wraps this handler: Sentry captures the error, then
  // calls it. Rethrowing hands the error back to Vue, which logs it.
  const previousErrorHandler = Vue.config.errorHandler
  Vue.config.errorHandler = function (err, vm, info) {
    if (isChunkLoadError(err) && recoverFromChunkError(currentUrl())) return
    if (previousErrorHandler) return previousErrorHandler.call(this, err, vm, info)
    throw err
  }
  // Not preventDefault(): Sentry's own handler still reports it.
  window.addEventListener('unhandledrejection', (event) => {
    if (isChunkLoadError(event.reason)) recoverFromChunkError(currentUrl())
  })
  // Vue 2 swallows a failed lazy component inside a view (production builds
  // log nothing, the component just does not render), so neither handler
  // above sees it. The failed <script>/<link> element does reach window in
  // the capture phase. Deferred, so router.onError, which knows the target
  // route, handles route chunks first.
  window.addEventListener('error', (event) => {
    const el = event.target
    if (!el || !(el.tagName === 'SCRIPT' || (el.tagName === 'LINK' && el.rel === 'stylesheet'))) return
    const url = el.src || el.href
    if (!isBuildFile(url)) return
    const failedAt = Date.now()
    setTimeout(() => {
      if (chunkErrorHandledAt >= failedAt) return
      if (!recoverFromChunkError(currentUrl())) Sentry.captureException(new Error(`Loading chunk failed: ${url}`))
    }, 100)
  }, true)
}

export function installAppUpdate (routerInstance, storeInstance) {
  router = routerInstance
  store = storeInstance
  // A flag left by a page that reloaded before its controllerchange arrived.
  try { window.sessionStorage.removeItem(INITIATED_KEY) } catch (e) { /* storage unavailable */ }

  router.afterEach((to, from) => {
    // Only the query or hash changed: the same page component stays mounted
    // and may hold edits.
    if (to.path === from.path) return
    typedSinceRouteChange = false
    // After the render, so a form being left has been destroyed and has
    // marked itself clean.
    Vue.nextTick(() => tryAutoUpdate(to.fullPath))
  })

  document.addEventListener('input', (event) => {
    if (event.isTrusted) typedSinceRouteChange = true
  }, true)

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') {
      hiddenAt = Date.now()
      return
    }
    const away = hiddenAt === null ? 0 : Date.now() - hiddenAt
    hiddenAt = null
    // Input typed on this page (also in editors that don't report unsaved
    // changes) would be lost without the user doing anything.
    if (away >= MIN_AWAY_MS && !typedSinceRouteChange) tryAutoUpdate(currentUrl())
  })

  store.watch((state, getters) => getters['unsavedChanges/hasUnsavedChanges'], (dirty) => {
    if (dirty) window.addEventListener('beforeunload', warnBeforeUnload)
    else window.removeEventListener('beforeunload', warnBeforeUnload)
  })

  installChunkErrorHandlers()
}
